<template>
  <div :class="gridClass">
    <label class="block text-sm font-medium text-gray-700 mb-1">
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <input
      :value="modelValue"
      @input="onInput"
      @blur="onBlur"
      type="email"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
      :class="inputClasses"
      autocomplete="email"
    />
    
    <!-- Message d'erreur -->
    <div v-if="errorMessage" class="mt-1 text-sm text-red-600">
      {{ errorMessage }}
    </div>
    
    <!-- Indicateur de chargement -->
    <div v-if="isCheckingEmail" class="mt-1 text-sm text-gray-500">
      Vérification en cours...
    </div>
    
    <!-- Indicateur de validation -->
    <div v-if="modelValue && !errorMessage && isValidEmail && !isCheckingEmail && !emailExists" class="mt-1 text-sm text-green-600">
      ✓ Email valide et disponible
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { EmailFieldProps } from '@/types/sellerRegister';
import { checkEmailExists } from '@/api/sellerRegister';

const props = defineProps<EmailFieldProps>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'validation-changed': [isValid: boolean];
}>();

// Constante pour la validation email (même que dans le login)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// État local pour la validation
const errorMessage = ref('');
const hasBeenTouched = ref(false);
const isCheckingEmail = ref(false);
const emailExists = ref(false);
let checkEmailTimeout: ReturnType<typeof setTimeout> | null = null;

// Classes CSS pour l'input
const inputClasses = computed(() => {
  const baseClasses = 'focus:outline-none';
  
  if (errorMessage.value) {
    return `${baseClasses} border-red-500`;
  } else if (props.modelValue && isValidEmail.value) {
    return `${baseClasses} border-green-500`;
  } else {
    return `${baseClasses} border-gray-300`;
  }
});

// Validation de l'email
const isValidEmail = computed(() => {
  if (!props.modelValue) return false;
  return EMAIL_REGEX.test(props.modelValue);
});

// Vérifier si l'email existe déjà sur le serveur
const checkEmailOnServer = async (email: string) => {
  if (!email || !isValidEmail.value) {
    emailExists.value = false;
    return;
  }

  isCheckingEmail.value = true;
  try {
    const exists = await checkEmailExists(email);
    emailExists.value = exists;
    
    if (exists) {
      errorMessage.value = 'Cet email est déjà utilisé';
      emit('validation-changed', false);
    } else {
      // Si l'email n'existe pas et que le format est valide, on valide
      if (isValidEmail.value) {
        errorMessage.value = '';
        emit('validation-changed', true);
      }
    }
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'email:', error);
    // En cas d'erreur, on ne bloque pas l'utilisateur
    emailExists.value = false;
  } finally {
    isCheckingEmail.value = false;
  }
};

// Validation complète avec messages d'erreur
const validateEmail = async () => {
  if (!props.modelValue) {
    errorMessage.value = '';
    emailExists.value = false;
    emit('validation-changed', false);
    return false;
  }
  
  if (!isValidEmail.value) {
    errorMessage.value = 'Veuillez saisir un email valide';
    emailExists.value = false;
    emit('validation-changed', false);
    return false;
  }
  
  // Si l'email existe déjà, on ne vérifie pas à nouveau
  if (emailExists.value) {
    errorMessage.value = 'Cet email est déjà utilisé';
    emit('validation-changed', false);
    return false;
  }
  
  // Vérifier sur le serveur avec debounce
  if (checkEmailTimeout) {
    clearTimeout(checkEmailTimeout);
  }
  
  checkEmailTimeout = setTimeout(() => {
    checkEmailOnServer(props.modelValue);
  }, 500); // Debounce de 500ms
  
  return true;
};

// Gérer les changements d'input
const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  
  emit('update:modelValue', value);
  
  // Réinitialiser l'état d'existence si l'email change
  if (emailExists.value) {
    emailExists.value = false;
    errorMessage.value = '';
  }
  
  // Valider et émettre l'état
  await validateEmail();
};

// Gérer la perte de focus
const onBlur = async () => {
  hasBeenTouched.value = true;
  
  // Valider seulement si le champ a été touché ou s'il y a une valeur
  if (hasBeenTouched.value || props.modelValue) {
    // Annuler le debounce en cours et vérifier immédiatement
    if (checkEmailTimeout) {
      clearTimeout(checkEmailTimeout);
      checkEmailTimeout = null;
    }
    await validateEmail();
  }
};

// Watcher pour la validation externe
watch(() => props.modelValue, (newValue) => {
  if (hasBeenTouched.value || newValue) {
    validateEmail();
  }
}, { immediate: true });

// Watcher pour la prop error externe
watch(() => props.error, (newError) => {
  if (newError && !errorMessage.value) {
    errorMessage.value = 'Email invalide';
  }
});
</script>
